import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Download, RefreshCw, FileX, Printer, Mail, X, XCircle, RotateCcw, Trash2, Send, AlertCircle, FileText, Repeat, Settings, Check } from 'lucide-react';
import MonthRangePicker from '../../components/MonthRangePicker';
import { documentsApi } from '../../utils/api';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import PrintPreviewModal from '../../components/PrintPreviewModal';
import DocumentDetailModal from '../../components/DocumentDetailModal';
import useAuthStore from '../../store/authStore';
import ConfirmModal from '../../components/ConfirmModal';
import { construirNDAnuladora } from '../../utils/anulacion';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
// Configuración de columnas disponibles para NC
const COLUMN_CONFIG = {
  consecutivo: { label: 'Consecutivo', default: true, fixed: true },
  fecha: { label: 'Fecha', default: true, fixed: true },
  cliente: { label: 'Cliente', default: true, fixed: true },
  docReferencia: { label: 'Doc. Referencia', default: true },
  fechaFEOriginal: { label: 'Fecha FE Original', default: true },
  subtotal: { label: 'SubTotal', default: true },
  impuestos: { label: 'Impuestos', default: true },
  total: { label: 'Total', default: true, fixed: true },
  tc: { label: 'TC', default: false },
  baseCRC: { label: 'Base CRC', default: false },
  ivaCRC: { label: 'IVA CRC', default: false },
  estado: { label: 'Estado', default: true, fixed: true }
};

// Obtener columnas visibles de localStorage o usar defaults
const getDefaultColumns = () => {
  const defaults = Object.fromEntries(
    Object.entries(COLUMN_CONFIG).map(([key, config]) => [key, config.default])
  );
  const saved = localStorage.getItem('notasCredito_columns');
  if (saved) {
    try {
      // Merge: respeta lo guardado, pero columnas nuevas (no guardadas) toman su default
      return { ...defaults, ...JSON.parse(saved) };
    } catch {
      return defaults;
    }
  }
  return defaults;
};

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-gray-100 text-gray-700' },
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  processing: { label: 'Procesando', color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Aceptada', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700' },
  error: { label: 'Error', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelada', color: 'bg-gray-200 text-gray-700' },
};

export default function NotasCredito() {
  const navigate = useNavigate();
  const { currentEmisor } = useAuthStore();
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Selection and modals
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  // Anular la NC con Nota de Débito
  const [showNDModal, setShowNDModal] = useState(false);
  const [creatingND, setCreatingND] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Modal mensaje Hacienda (para ver errores de rechazo)
  const [mensajeHaciendaModal, setMensajeHaciendaModal] = useState(null);

  // Modal cancelar documento rechazado
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingDoc, setCancellingDoc] = useState(false);

  // Eliminar NC cancelada
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // El botón Eliminar se muestra solo si la NC seleccionada está en estado Cancelado
  const canDeleteSelected = selectedDoc?.status === 'cancelled';

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(getDefaultColumns);
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    if (COLUMN_CONFIG[columnKey]?.fixed) return;
    setVisibleColumns(prev => {
      const updated = { ...prev, [columnKey]: !prev[columnKey] };
      localStorage.setItem('notasCredito_columns', JSON.stringify(updated));
      return updated;
    });
  };

  // Modal y estados para gestionar borradores
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftDoc, setDraftDoc] = useState(null);
  const [deletingDraft, setDeletingDraft] = useState(false);
  const [submittingDraft, setSubmittingDraft] = useState(false);

  // Filtro de período — soporta modo 'mes' (con flechas ◀ ▶) o 'rango' (custom).
  // El componente MonthRangePicker maneja la UI completa.
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo('NotasCredito', () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, n.getMonth() + 1, 0).getDate();
    return {
      modo: 'mes',
      periodo: `${y}-${m}`,
      desde: `${y}-${m}-01`,
      hasta: `${y}-${m}-${String(last).padStart(2, '0')}`
    };
  });

  const handleConfirmND = async () => {
    if (!selectedDoc || creatingND) return;
    setCreatingND(true);
    try {
      const docResponse = await documentsApi.getById(selectedDoc.id);
      const nc = docResponse.data?.data;
      if (!nc) {
        toast.error('Error al cargar la Nota de Crédito');
        return;
      }
      await documentsApi.create(construirNDAnuladora(nc), true);
      toast.success('Nota de Débito creada y enviada a Hacienda');
      setSelectedDoc(null);
      cargarDocumentos(pagination.page);
    } catch (error) {
      console.error('Error creating ND:', error);
      toast.error(error.response?.data?.error || 'Error al crear la Nota de Débito');
    } finally {
      setCreatingND(false);
    }
  };

  const cargarDocumentos = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20, tipo: '03' }; // 03 = Nota de Crédito
      if (search) params.search = search;
      if (filtroFecha?.desde) params.desde = filtroFecha.desde;
      if (filtroFecha?.hasta) params.hasta = filtroFecha.hasta;

      const response = await documentsApi.list(params);
      setDocumentos(response.data.data || []);
      setPagination(response.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (error) {
      console.error('Error cargando notas de crédito:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDocumentos();
  }, [filtroFecha]);

  // Auto-refresh every 30 seconds (only when there are documents in "processing" state)
  const hasProcessingDocs = documentos.some(d => d.status === 'processing');

  useEffect(() => {
    if (!autoRefresh || !hasProcessingDocs) return;

    const interval = setInterval(() => {
      cargarDocumentos(pagination.page);
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, hasProcessingDocs, pagination.page]);

  // Delete cancelled document handler
  const handleDeleteCancelled = () => {
    if (!canDeleteSelected) return;
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDoc) return;
    setDeleting(true);
    try {
      await documentsApi.deleteCancelled(selectedDoc.id);
      toast.success('Nota de Crédito cancelada eliminada');
      setShowDeleteModal(false);
      setSelectedDoc(null);
      cargarDocumentos(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar documento');
    } finally {
      setDeleting(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    cargarDocumentos(1);
  };

  const formatCurrency = (amount, currency = 'CRC') => {
    const symbol = currency === 'USD' ? '$' : '₡';
    const v = parseFloat(amount) || 0;
    const sign = v < 0 ? '-' : '';
    return sign + symbol + new Intl.NumberFormat('es-CR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(v));
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const s = String(date);
    // Fecha pura YYYY-MM-DD → formatear sin pasar por Date (evita shift de zona)
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split('-');
      return `${d}/${m}/${y}`;
    }
    return new Date(s).toLocaleDateString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Selection handler
  const handleSelectDoc = (doc) => {
    if (selectedDoc?.id === doc.id) {
      setSelectedDoc(null);
    } else {
      setSelectedDoc(doc);
    }
  };

  // Action handlers
  const handlePrint = () => {
    if (!selectedDoc) return;
    setShowPrintPreview(true);
  };

  const handleSendEmail = async () => {
    if (!selectedDoc) return;
    if (selectedDoc.status !== 'accepted') {
      toast.error('Solo se pueden enviar por email documentos aceptados');
      return;
    }

    setSendingEmail(true);
    try {
      await documentsApi.sendEmail(selectedDoc.id);
      toast.success('Email enviado al cliente');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleViewDetails = () => {
    if (!selectedDoc) return;
    setShowDetailModal(true);
  };

  // Abrir modal de borrador
  const handleOpenDraftModal = (doc) => {
    setDraftDoc(doc);
    setShowDraftModal(true);
  };

  // Eliminar borrador
  const handleDeleteDraft = async () => {
    if (!draftDoc) return;

    setDeletingDraft(true);
    try {
      await documentsApi.delete(draftDoc.id);
      toast.success('Borrador eliminado');
      setShowDraftModal(false);
      setDraftDoc(null);
      setSelectedDoc(null);
      cargarDocumentos(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar borrador');
    } finally {
      setDeletingDraft(false);
    }
  };

  // Enviar borrador a Hacienda
  const handleSubmitDraft = async () => {
    if (!draftDoc) return;

    setSubmittingDraft(true);
    try {
      await documentsApi.submit(draftDoc.id);
      toast.success('Nota de Crédito enviada a Hacienda');
      setShowDraftModal(false);
      setDraftDoc(null);
      setSelectedDoc(null);
      cargarDocumentos(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar documento');
    } finally {
      setSubmittingDraft(false);
    }
  };

  const handleDownloadXml = async () => {
    if (!selectedDoc) return;

    try {
      const response = await documentsApi.downloadXml(selectedDoc.id);

      // Check content-type to determine if it's a ZIP or XML
      const contentType = response.headers['content-type'] || '';
      const isZip = contentType.includes('application/zip');

      // Create blob with appropriate type
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isZip
        ? `${selectedDoc.consecutivo}.zip`
        : `${selectedDoc.consecutivo}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Show appropriate message
      if (isZip) {
        toast.success('XMLs descargados (documento enviado + respuesta Hacienda)');
      } else {
        toast.success('XML descargado');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al descargar XML');
    }
  };

  // Totales agrupados por moneda — al ser NCs todos los montos se muestran como NEGATIVOS
  // (créditos sobre la FE original). Foot sticky de la tabla los muestra al pie.
  const totalesPorMoneda = (() => {
    const totals = {};
    for (const doc of documentos) {
      const moneda = doc.codigo_moneda || doc.codigoMoneda || 'CRC';
      const tipoCambio = parseFloat(doc.tipo_cambio || doc.tipoCambio) || 1;
      if (!totals[moneda]) totals[moneda] = { subtotal: 0, impuestos: 0, total: 0, baseCRC: 0, ivaCRC: 0, count: 0 };
      const subtotal = parseFloat(doc.total_venta_neta || doc.totales?.totalVentaNeta) || 0;
      const impuestos = parseFloat(doc.total_impuesto || doc.totales?.totalImpuesto) || 0;
      const total = parseFloat(doc.total_comprobante || doc.totales?.totalComprobante) || 0;
      totals[moneda].subtotal -= subtotal;     // negativo (NC = crédito)
      totals[moneda].impuestos -= impuestos;
      totals[moneda].total -= total;
      // Colones: dato guardado en las líneas al emitir (no se recalcula con subtotal × TC,
      // que además contaba 911 y Cruz Roja como IVA en telecomunicaciones)
      totals[moneda].baseCRC -= parseFloat(doc.totalBaseImponibleColones ?? doc.total_base_imponible_colones) || 0;
      totals[moneda].ivaCRC -= parseFloat(doc.totalIvaColones ?? doc.total_iva_colones) || 0;
      totals[moneda].count++;
    }
    return totals;
  })();

  return (
    <div className="flex flex-col h-[var(--alto-contenido)] min-w-0">
      {/* Action Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 shrink-0">
        <div className="flex items-center justify-center gap-1 overflow-x-auto">
          {/* Imprimir */}
          <button
            onClick={handlePrint}
            disabled={!selectedDoc}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-slate-400 text-slate-600 hover:bg-slate-50 hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <Printer className="h-5 w-5 mb-1" />
            <span>Imprimir</span>
          </button>

          {/* Enviar por Email */}
          <button
            onClick={handleSendEmail}
            disabled={!selectedDoc || sendingEmail || selectedDoc?.status !== 'accepted'}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <Mail className="h-5 w-5 mb-1" />
            <span>{sendingEmail ? 'Enviando...' : 'Email'}</span>
          </button>

          <div className="w-px h-12 bg-gray-300 mx-1" />

          {/* Descargar XML */}
          <button
            onClick={handleDownloadXml}
            disabled={!selectedDoc}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-teal-400 text-teal-600 hover:bg-teal-50 hover:border-teal-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <Download className="h-5 w-5 mb-1" />
            <span>XML</span>
          </button>

          {/* Ver Detalles */}
          <button
            onClick={handleViewDetails}
            disabled={!selectedDoc}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-purple-400 text-purple-600 hover:bg-purple-50 hover:border-purple-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <Eye className="h-5 w-5 mb-1" />
            <span>Detalles</span>
          </button>

          <div className="w-px h-12 bg-gray-300 mx-1" />

          {/* Anular esta NC con Nota de Débito */}
          <button
            onClick={() => setShowNDModal(true)}
            disabled={!selectedDoc || selectedDoc?.status !== 'accepted' || !!selectedDoc?.ndAnulacion || creatingND}
            title={selectedDoc?.ndAnulacion
              ? `Esta NC ya tiene la ND ${selectedDoc.ndAnulacion.consecutivo || ''} que la anula`
              : 'Emitir una Nota de Débito que anula esta Nota de Crédito'}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-red-400 text-red-600 hover:bg-red-50 hover:border-red-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <XCircle className="h-5 w-5 mb-1" />
            <span>Anular con ND</span>
          </button>

          {/* Eliminar Cancelado - solo visible si se puede eliminar */}
          {canDeleteSelected && (
            <>
              <div className="w-px h-12 bg-gray-300 mx-1" />
              <button
                onClick={handleDeleteCancelled}
                disabled={deleting}
                className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-red-400 text-red-600 hover:bg-red-50 hover:border-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="h-5 w-5 mb-1" />
                <span>{deleting ? 'Eliminando...' : 'Eliminar'}</span>
              </button>
            </>
          )}
        </div>

        {/* Selected document info */}
        {selectedDoc && (
          <div className="mt-2 pt-2 border-t border-gray-100 text-sm text-gray-500 flex items-center justify-between">
            <div>
              Seleccionado: <span className="font-medium text-gray-900">{selectedDoc.consecutivo}</span>
              {(selectedDoc.receptor_nombre || selectedDoc.receptorNombre) && (
                <span className="ml-2">- {selectedDoc.receptor_nombre || selectedDoc.receptorNombre}</span>
              )}
            </div>
            <button
              onClick={() => setSelectedDoc(null)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por consecutivo, cliente..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </form>
          {/* Selector de período — mes (◀ ▶) o rango personalizado via popup */}
          <MonthRangePicker
            value={filtroFecha}
            onChange={setFiltroFecha}
            onRefresh={() => cargarDocumentos(1)}
          />
          <label
            className="flex items-center gap-1.5 text-sm cursor-pointer select-none"
            title={autoRefresh
              ? (hasProcessingDocs ? 'Auto-check activo (cada 30s)' : 'Auto-check habilitado (sin NC procesando)')
              : 'Auto-check desactivado'
            }
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className={`flex items-center gap-1 ${autoRefresh && hasProcessingDocs ? 'text-green-600 font-medium' : 'text-gray-600'}`}>
              Auto
              {autoRefresh && hasProcessingDocs && (
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              )}
            </span>
          </label>

          {/* Column Selector */}
          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className={clsx(
                "p-2 border rounded-lg hover:bg-gray-50",
                showColumnSelector ? "border-blue-500 bg-blue-50" : "border-gray-300"
              )}
              title="Configurar columnas"
            >
              <Settings className="h-4 w-4 text-gray-600" />
            </button>
            {showColumnSelector && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowColumnSelector(false)} />
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px]">
                  <div className="px-3 py-2 border-b border-gray-100 font-medium text-sm text-gray-700">
                    Columnas
                  </div>
                  <div className="py-1 max-h-64 overflow-y-auto">
                    {Object.entries(COLUMN_CONFIG).map(([key, config]) => (
                      <button
                        key={key}
                        onClick={() => toggleColumn(key)}
                        disabled={config.fixed}
                        className={clsx(
                          "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-gray-50",
                          config.fixed && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <span className={clsx(
                          "w-4 h-4 rounded border flex items-center justify-center",
                          visibleColumns[key] ? "bg-blue-600 border-blue-600" : "border-gray-300"
                        )}>
                          {visibleColumns[key] && <Check className="h-3 w-3 text-white" />}
                        </span>
                        <span>{config.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabla — sticky con scroll interno + tfoot con totales sticky al pie */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-gray-500">Cargando...</p>
          </div>
        ) : documentos.length === 0 ? (
          <div className="p-8 text-center">
            <FileX className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No se encontraron notas de crédito</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_rgba(229,231,235,1)]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Consecutivo</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Fecha</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Cliente</th>
                  {visibleColumns.docReferencia && (
                    <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Doc. Referencia</th>
                  )}
                  {visibleColumns.fechaFEOriginal && (
                    <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Fecha FE Original</th>
                  )}
                  {visibleColumns.subtotal && (
                    <th className="text-right px-3 py-2 font-medium text-gray-700 whitespace-nowrap">SubTotal</th>
                  )}
                  {visibleColumns.impuestos && (
                    <th className="text-right px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Impuestos</th>
                  )}
                  <th className="text-right px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Total</th>
                  {visibleColumns.tc && (
                    <th className="text-center px-2 py-2 font-medium text-gray-500 whitespace-nowrap text-xs">TC</th>
                  )}
                  {visibleColumns.baseCRC && (
                    <th className="text-right px-2 py-2 font-medium text-gray-500 whitespace-nowrap text-xs">Base CRC</th>
                  )}
                  {visibleColumns.ivaCRC && (
                    <th className="text-right px-2 py-2 font-medium text-gray-500 whitespace-nowrap text-xs">IVA CRC</th>
                  )}
                  <th className="text-center px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {documentos.map((doc) => {
                  const isSelected = selectedDoc?.id === doc.id;
                  const moneda = doc.codigo_moneda || doc.codigoMoneda || 'CRC';
                  const tipoCambio = parseFloat(doc.tipo_cambio || doc.tipoCambio) || 1;
                  const subtotal = parseFloat(doc.total_venta_neta || doc.totales?.totalVentaNeta) || 0;
                  const impuestos = parseFloat(doc.total_impuesto || doc.totales?.totalImpuesto) || 0;
                  const total = parseFloat(doc.total_comprobante || doc.totales?.totalComprobante) || 0;
                  const baseCRC = parseFloat(doc.totalBaseImponibleColones ?? doc.total_base_imponible_colones) || 0;
                  const ivaCRC = parseFloat(doc.totalIvaColones ?? doc.total_iva_colones) || 0;

                  return (
                    <tr
                      key={doc.id}
                      onClick={() => handleSelectDoc(doc)}
                      className={clsx(
                        'cursor-pointer transition-colors',
                        isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                      )}
                    >
                      {/* Consecutivo */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-gray-900">{doc.consecutivo}</span>
                          {doc.ndAnulacion && (
                            <span
                              className={clsx(
                                'text-[9px] px-1 py-px rounded font-medium whitespace-nowrap',
                                doc.ndAnulacion.estado === 'aceptado' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'
                              )}
                              title={`ND ${doc.ndAnulacion.consecutivo || ''} (${doc.ndAnulacion.estado})`}
                            >
                              {doc.ndAnulacion.estado === 'aceptado' ? 'ANULADA POR ND' : 'ND EN TRÁMITE'}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Fecha */}
                      <td className="px-2 py-2 text-gray-600 whitespace-nowrap text-sm">
                        {formatDate(doc.fecha_emision || doc.fechaEmision)}
                      </td>
                      {/* Cliente */}
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">
                          {doc.receptor_nombre || doc.receptorNombre || 'Sin cliente'}
                        </div>
                      </td>
                      {/* Doc. Referencia */}
                      {visibleColumns.docReferencia && (
                        <td className="px-3 py-2">
                          <span className="text-xs text-gray-600 font-mono">
                            {doc.referencia_consecutivo || doc.referencia?.consecutivo || '-'}
                          </span>
                        </td>
                      )}
                      {/* Fecha FE Original */}
                      {visibleColumns.fechaFEOriginal && (
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                          {formatDate(doc.referencia?.fecha || doc.referencia_fecha)}
                        </td>
                      )}
                      {/* SubTotal */}
                      {visibleColumns.subtotal && (
                        <td className="px-3 py-2 text-right text-sm text-orange-600">
                          -{formatCurrency(subtotal, moneda)}
                        </td>
                      )}
                      {/* Impuestos */}
                      {visibleColumns.impuestos && (
                        <td className="px-3 py-2 text-right text-sm text-orange-600">
                          -{formatCurrency(impuestos, moneda)}
                        </td>
                      )}
                      {/* Total */}
                      <td className="px-3 py-2 text-right text-sm font-bold text-orange-600">
                        -{formatCurrency(total, moneda)}
                      </td>
                      {/* TC */}
                      {visibleColumns.tc && (
                        <td className="px-2 py-2 text-center text-sm text-gray-500">
                          {moneda === 'USD' ? `₡${tipoCambio.toFixed(2)}` : '-'}
                        </td>
                      )}
                      {/* Base CRC */}
                      {visibleColumns.baseCRC && (
                        <td className="px-2 py-2 text-right text-sm text-gray-500">
                          {moneda === 'USD' ? `-₡${baseCRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}` : '-'}
                        </td>
                      )}
                      {/* IVA CRC */}
                      {visibleColumns.ivaCRC && (
                        <td className="px-2 py-2 text-right text-sm text-gray-500">
                          {moneda === 'USD' ? `-₡${ivaCRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}` : '-'}
                        </td>
                      )}
                      <td className="py-3 px-3 text-center">
                        <span
                          className={clsx(
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                            STATUS_CONFIG[doc.status]?.color || 'bg-gray-100 text-gray-700',
                            (doc.status === 'error' || doc.status === 'rejected') && doc.mensajeHacienda && 'cursor-pointer hover:ring-2 hover:ring-red-300'
                          )}
                          onClick={(e) => {
                            if ((doc.status === 'error' || doc.status === 'rejected') && doc.mensajeHacienda) {
                              e.stopPropagation();
                              setMensajeHaciendaModal({ doc, mensaje: doc.mensajeHacienda });
                            }
                          }}
                          title={(doc.status === 'error' || doc.status === 'rejected') && doc.mensajeHacienda
                            ? 'Clic para ver el mensaje de Hacienda'
                            : undefined
                          }
                        >
                          {STATUS_CONFIG[doc.status]?.label || doc.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Totales por moneda — sticky al pie. Montos en negativo (NC = crédito). */}
              {Object.keys(totalesPorMoneda).length > 0 && (
                <tfoot className="sticky bottom-0 z-10 bg-gray-100 border-t-2 border-gray-400 shadow-[0_-1px_0_rgba(156,163,175,1)]">
                  {Object.entries(totalesPorMoneda).map(([moneda, totals]) => (
                    <tr key={moneda}>
                      {/* Consecutivo: indicador del subtotal por moneda */}
                      <td className="px-3 py-2 text-xs font-semibold text-gray-600">
                        Total {moneda} ({totals.count})
                      </td>
                      {/* Fecha */}
                      <td className="px-2 py-2"></td>
                      {/* Cliente */}
                      <td className="px-3 py-2"></td>
                      {/* Doc Referencia */}
                      {visibleColumns.docReferencia && <td className="px-3 py-2"></td>}
                      {/* Fecha FE Original */}
                      {visibleColumns.fechaFEOriginal && <td className="px-3 py-2"></td>}
                      {/* SubTotal */}
                      {visibleColumns.subtotal && (
                        <td className="px-3 py-2 text-right text-sm font-semibold text-orange-700">
                          {formatCurrency(totals.subtotal, moneda)}
                        </td>
                      )}
                      {/* Impuestos */}
                      {visibleColumns.impuestos && (
                        <td className="px-3 py-2 text-right text-sm font-semibold text-orange-700">
                          {formatCurrency(totals.impuestos, moneda)}
                        </td>
                      )}
                      {/* Total */}
                      <td className="px-3 py-2 text-right text-sm font-bold text-orange-700">
                        {formatCurrency(totals.total, moneda)}
                      </td>
                      {/* TC */}
                      {visibleColumns.tc && <td className="px-2 py-2"></td>}
                      {/* Base CRC */}
                      {visibleColumns.baseCRC && (
                        <td className="px-2 py-2 text-right text-sm font-semibold text-gray-600">
                          -₡{Math.abs(totals.baseCRC).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      )}
                      {/* IVA CRC */}
                      {visibleColumns.ivaCRC && (
                        <td className="px-2 py-2 text-right text-sm font-semibold text-gray-600">
                          -₡{Math.abs(totals.ivaCRC).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      )}
                      {/* Estado */}
                      <td className="px-3 py-2"></td>
                    </tr>
                  ))}
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* Paginación */}
        {pagination.pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <p className="text-sm text-gray-500">
              Mostrando {documentos.length} de {pagination.total}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => cargarDocumentos(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm bg-white disabled:opacity-50 hover:bg-gray-50"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                {pagination.page} / {pagination.pages}
              </span>
              <button
                onClick={() => cargarDocumentos(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded text-sm bg-white disabled:opacity-50 hover:bg-gray-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirmar anulación de la NC con Nota de Débito */}
      <ConfirmModal
        isOpen={showNDModal}
        onClose={() => setShowNDModal(false)}
        onConfirm={handleConfirmND}
        variant="danger"
        title="Anular Nota de Crédito con Nota de Débito"
        message={`Se emitirá y enviará a Hacienda una Nota de Débito por el mismo monto que anula la NC ${selectedDoc?.consecutivo || ''} (${selectedDoc?.receptor_nombre || selectedDoc?.receptorNombre || ''}). Esta acción no se puede deshacer.`}
        confirmText="Emitir Nota de Débito"
      />

      {/* Print Preview Modal */}
      {showPrintPreview && selectedDoc && (
        <PrintPreviewModal
          documentId={selectedDoc.id}
          emisor={currentEmisor}
          onClose={() => setShowPrintPreview(false)}
        />
      )}

      {/* Document Detail Modal */}
      {showDetailModal && selectedDoc && (
        <DocumentDetailModal
          documentId={selectedDoc.id}
          emisor={currentEmisor}
          onClose={() => setShowDetailModal(false)}
        />
      )}

      {/* Modal Mensaje Hacienda (errores/rechazos) */}
      {mensajeHaciendaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setMensajeHaciendaModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b bg-red-50 rounded-t-xl">
              <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Respuesta de Hacienda
              </h3>
              <button onClick={() => setMensajeHaciendaModal(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <div className="mb-3 text-sm text-gray-600">
                <strong>Consecutivo:</strong> {mensajeHaciendaModal.doc?.consecutivo}
              </div>
              <div className="mb-3 text-sm text-gray-600">
                <strong>Cliente:</strong> {mensajeHaciendaModal.doc?.receptor_nombre || mensajeHaciendaModal.doc?.receptorNombre || 'Sin cliente'}
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                <pre className="text-sm text-red-800 whitespace-pre-wrap font-mono select-text">
                  {mensajeHaciendaModal.mensaje}
                </pre>
              </div>
              <p className="mt-2 text-xs text-gray-500">Puedes seleccionar el texto con el mouse para copiarlo</p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(mensajeHaciendaModal.mensaje);
                  toast.success('Mensaje copiado');
                }}
                className="px-4 py-2 text-sm border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                Copiar Mensaje
              </button>
              {mensajeHaciendaModal.doc?.status === 'rejected' && (
                <>
                  <button
                    onClick={() => {
                      const docId = mensajeHaciendaModal.doc.id;
                      setMensajeHaciendaModal(null);
                      navigate(`/facturacion/nueva?cargar=${docId}&rejected=1`);
                    }}
                    className="px-4 py-2 text-sm border border-teal-500 text-teal-600 rounded-lg hover:bg-teal-50 flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Editar y Corregir
                  </button>
                  <button
                    onClick={async () => {
                      const docId = mensajeHaciendaModal.doc.id;
                      setMensajeHaciendaModal(null);
                      try {
                        toast.loading('Reenviando a Hacienda...', { id: 'reenvio' });
                        await documentsApi.submit(docId);
                        toast.success('Documento reenviado a Hacienda', { id: 'reenvio' });
                        cargarDocumentos(pagination.page);
                      } catch (error) {
                        toast.error(error.response?.data?.error || 'Error al reenviar', { id: 'reenvio' });
                      }
                    }}
                    className="px-4 py-2 text-sm border border-orange-500 text-orange-600 rounded-lg hover:bg-orange-50 flex items-center gap-2"
                  >
                    <Repeat className="h-4 w-4" />
                    Reenviar sin cambios
                  </button>
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="px-4 py-2 text-sm border border-red-500 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancelar Documento
                  </button>
                </>
              )}
              <button
                onClick={() => setMensajeHaciendaModal(null)}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cancelar Documento Rechazado */}
      {showCancelModal && mensajeHaciendaModal?.doc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowCancelModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-4 border-b bg-red-50 rounded-t-xl">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Cancelar Nota de Crédito</h2>
                <p className="text-sm text-gray-600">Marca la NC rechazada como anulada</p>
              </div>
              <button
                onClick={() => setShowCancelModal(false)}
                className="ml-auto p-2 hover:bg-red-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500 mb-1">Documento a cancelar:</p>
                <p className="font-mono font-medium">{mensajeHaciendaModal.doc.consecutivo}</p>
                <p className="text-sm text-gray-600 mt-1">{mensajeHaciendaModal.doc.receptor_nombre || mensajeHaciendaModal.doc.receptorNombre}</p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">El documento será cancelado definitivamente:</p>
                    <ul className="list-disc list-inside text-amber-700 space-y-1">
                      <li>El documento quedará marcado como "Cancelado"</li>
                      <li>El consecutivo NO se reutiliza (ya quedó registrado en Hacienda)</li>
                      <li>Luego podrás eliminarlo del sistema</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={cancellingDoc}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Volver
              </button>
              <button
                onClick={async () => {
                  const doc = mensajeHaciendaModal.doc;
                  setCancellingDoc(true);
                  try {
                    await documentsApi.cancel(doc.id, doc.mensajeHacienda || 'Documento rechazado - cancelado por usuario');
                    toast.success('Documento cancelado. Consecutivo reciclado.');
                    setShowCancelModal(false);
                    setMensajeHaciendaModal(null);
                    cargarDocumentos(pagination.page);
                  } catch (error) {
                    toast.error(error.response?.data?.error || 'Error al cancelar');
                  } finally {
                    setCancellingDoc(false);
                  }
                }}
                disabled={cancellingDoc}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                {cancellingDoc ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Cancelando...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    Confirmar Cancelación
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestionar Borrador NC */}
      {showDraftModal && draftDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDraftModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-4 border-b bg-gray-50 rounded-t-xl">
              <div className="p-2 bg-gray-200 rounded-lg">
                <FileText className="h-6 w-6 text-gray-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Nota de Crédito - Borrador</h2>
                <p className="text-sm text-gray-600">No enviada a Hacienda</p>
              </div>
              <button
                onClick={() => setShowDraftModal(false)}
                className="ml-auto p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Info del documento */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-xs text-gray-500">Nota de Crédito</span>
                    <p className="font-mono font-medium text-gray-900">{draftDoc.consecutivo || 'Sin consecutivo'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-red-600">
                      -{formatCurrency(draftDoc.total_comprobante || draftDoc.totales?.totalComprobante, draftDoc.codigo_moneda || draftDoc.codigoMoneda)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(draftDoc.fecha_emision || draftDoc.fechaEmision || draftDoc.created_at)}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">{draftDoc.receptor_nombre || draftDoc.receptorNombre || 'Sin cliente'}</p>
              </div>

              {/* Documento de referencia (qué FE se iba a anular) */}
              {(draftDoc.referencia_numero || draftDoc.referencia?.numero) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-600 font-medium mb-1">Esta NC referencia a:</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-mono text-sm text-gray-800">
                        {draftDoc.referencia?.consecutivo || draftDoc.referencia_consecutivo || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-600 truncate max-w-[200px]">
                        Clave: {(draftDoc.referencia_numero || draftDoc.referencia?.numero)?.slice(0, 25)}...
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      (draftDoc.referencia?.codigo || draftDoc.referencia_codigo) === '01' ? 'bg-red-100 text-red-700' :
                      (draftDoc.referencia?.codigo || draftDoc.referencia_codigo) === '02' ? 'bg-blue-100 text-blue-700' :
                      (draftDoc.referencia?.codigo || draftDoc.referencia_codigo) === '03' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {(draftDoc.referencia?.codigo || draftDoc.referencia_codigo) === '01' ? 'Anulación' :
                       (draftDoc.referencia?.codigo || draftDoc.referencia_codigo) === '02' ? 'Corrección' :
                       (draftDoc.referencia?.codigo || draftDoc.referencia_codigo) === '03' ? 'Descuento' : 'Otro'}
                    </span>
                  </div>
                  {(draftDoc.notas || draftDoc.referencia?.razon) && (
                    <p className="text-xs text-gray-600 mt-2 italic">"{draftDoc.notas || draftDoc.referencia?.razon}"</p>
                  )}
                </div>
              )}

              {/* Mensaje informativo */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
                <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">Esta Nota de Crédito no ha sido enviada a Hacienda.</p>
                  <p className="text-blue-600">Puede enviarla ahora o eliminarla si ya no es necesaria.</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={handleDeleteDraft}
                disabled={deletingDraft || submittingDraft}
                className="flex-1 px-4 py-2.5 border border-red-400 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deletingDraft ? (
                  <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Eliminar
              </button>
              <button
                onClick={handleSubmitDraft}
                disabled={deletingDraft || submittingDraft}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submittingDraft ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Enviar a Hacienda
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Eliminar NC Cancelada */}
      {showDeleteModal && selectedDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => !deleting && setShowDeleteModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-4 border-b bg-red-50 rounded-t-xl">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-800">Eliminar NC Cancelada</h3>
                <p className="text-sm text-red-600">Esta acción no se puede deshacer</p>
              </div>
            </div>

            <div className="p-5">
              <div className="bg-gray-50 rounded-lg p-4 border">
                <p className="text-sm text-gray-500 mb-2">Nota de Crédito a eliminar:</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono font-medium text-gray-900">{selectedDoc.consecutivo}</p>
                    <p className="text-sm text-gray-600">{selectedDoc.receptor_nombre || selectedDoc.receptorNombre || 'Sin cliente'}</p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-700">
                      CANCELADA
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> Esta NC está cancelada (no es un documento válido ante Hacienda). Se eliminará solo del sistema; no afecta el consecutivo.
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 border border-red-500 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Sí, Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
